import { transformPdpProduct } from "~/features/productDetails/transformers/PdpPageProductDataTransformer";
import { type PdpPageProductDataDto } from "~/features/productDetails/dto/PdpPageProductDataDto";
import { DesktopProductDetailsPage } from "~/features/productDetails/components/DesktopProductDetailsPage";

import { MobileProductDetailsPage } from "~/features/productDetails/components/MobileProductDetailsPage";
import useMediaQuery from "~/common/hooks/useMediaQuery";
import { useOnMount } from "~/common/hooks/useOnMount";
import { loadPDPData } from "~/features/productDetails/redux/actions";
import { useDispatch } from "react-redux";
import { useFetch } from "~/common/hooks/useFetch";
import { fetchPreferredProducts } from "~/features/productDetails/api/fetchPreferredProducts";
import { addMessage } from "~/features/toasts/redux/actions";
import { MEDIA_QUERY_MIN_DESKTOP_S } from "~/common/utils/screenSizes";
import { RecentlyViewedProducts } from "~/features/RecentlyViewedProducts/RecentlyViewedProducts";
import { useTranslation } from "react-i18next";
import { type TechnicalInfoDataDTO } from "~/features/search/models/SearchResultsDTO";
import { useState } from "react";
import { trackPdpView } from "~/app/analytics/ga/abandonment";
import last from "lodash/last";
import { useRelatedProducts } from "~/domains/product/redux/relatedProducts/useRelatedProducts";
import { addBaseProductDataAction } from "~/domains/product/redux/actions";
import { useFrequentlyBoughtTogetherProducts } from "~/features/productDetails/components/FrequentlyBoughtTogetherSection/useFrequentlyBoughtTogetherProducts";
import type {
  PdpPageProductData,
  PreferredProductData,
  RelatedProductData,
} from "~/features/productDetails/models/PdpPageProductData";
import type { LoadableEntry } from "~/app/store/loadableTypes";
import type { SrpProductData } from "~/utils/models";
import { FrequentlyBoughtTogetherSection } from "~/features/productDetails/components/FrequentlyBoughtTogetherSection/FrequentlyBoughtTogetherSection";
import { PurchasePlaceProvider } from "~/app/analytics/ga/recommendations/RecommendationContexts";
import RelatedProductsBlock from "~/features/productDetails/components/RelatedProducts/RelatedProductsBlock";
import { PDP_RESTRICTION_URL_PARAM } from "~/features/PackageRestriction/utils/constants";
import { useCalculateRestrictedCode } from "~/features/PackageRestriction/hooks/useCalculateRestrictedCode";
import { PackageRestrictionContext } from "~/features/PackageRestriction/PackageRestrictionContext";
import { type AddToCartFormData, useAddToCartForm } from "~/features/addToCart/hooks/useAddToCartNew";
import { makeSalesAwarePackCodeProvider } from "~/features/salesCampaign/utils/salesAwarePackCodeProvider";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { FormProvider, type UseFormReturn } from "react-hook-form";
import { DisplayMMDAssistantProvider } from "~/features/productDetails/context/DisplayMMDAssistantContext";
import CircleSpinner from "~/common/components/CircleSpinner/CircleSpinner";
import { userIsLoggedIn, userIsRegistered } from "~/domains/user/utils/roles";
import { PreferredProductsSection as PreferredProductsSectionV2 } from "~/features/productDetails/components/PreferredProducts/v2/PreferredProductsSection";
import PreferredProductsSection from "~/features/productDetails/components/PreferredProducts/v1/PreferredProductsSection";
import { VariantContextProvider } from "~/features/productDetails/context/VariantContext";

interface Props {
  product: PdpPageProductData;
  showFBT: boolean;
}

export interface CommonProductDetailsPageProps {
  productCode: string;
  designation: string;
  relatedProducts: RelatedProductData[];
  relatedProductsLoading: boolean;
  preferredProducts: LoadableEntry<{
    preferred: PreferredProductData[];
    sales: SrpProductData[];
  }>;
}

type InnerProductDetailsPageProps = CommonProductDetailsPageProps & {
  showFBT: boolean;
};

const InnerProductDetailsPage = ({ showFBT, ...fullProps }: InnerProductDetailsPageProps) => {
  const params = new URLSearchParams(window.location.search);
  const query = params.get(PDP_RESTRICTION_URL_PARAM) ?? "";
  const restrictedCode = useCalculateRestrictedCode(query, fullProps.productCode);

  const [formProps, productDescriptor] = useAddToCartForm({
    productCode: fullProps.productCode,
    initialPackCodeProvider: makeSalesAwarePackCodeProvider(restrictedCode),
  }) as [UseFormReturn<AddToCartFormData>, ProductDescriptor<PdpPageProductData>];

  const isDesktop = useMediaQuery(MEDIA_QUERY_MIN_DESKTOP_S);
  const fbtProps = useFrequentlyBoughtTogetherProducts(
    fullProps.productCode,
    showFBT && !productDescriptor.base.externalProduct,
  );
  const postSummarySlot = fbtProps.showFBTComponent && (
    <FrequentlyBoughtTogetherSection
      className={"scha__pdp__frequently-bought-together"}
      loading={fbtProps.loadingFBT}
      mainProduct={fbtProps.mainProduct}
      firstProduct={fbtProps.firstProduct}
      secondProduct={fbtProps.secondProduct}
    />
  );

  const { relatedProducts, relatedProductsLoading, preferredProducts } = fullProps;
  const product = productDescriptor.base;

  let preferredProductsBlock;
  if (preferredProducts.state === "loading") {
    preferredProductsBlock = (
      <div className="scha__related-products">
        <CircleSpinner />
      </div>
    );
  } else if (window.SCHA.feature.preferredProductsDesign === "A") {
    preferredProductsBlock = (
      <PreferredProductsSectionV2
        productFamilyName={product.preferredProductsFamilyName}
        products={preferredProducts.data?.preferred ?? []}
        showAddToCart={true}
      />
    );
  } else if (window.SCHA.feature.preferredProductsDesign === "B") {
    preferredProductsBlock = (
      <PreferredProductsSectionV2
        productFamilyName={product.preferredProductsFamilyName}
        products={preferredProducts.data?.preferred ?? []}
        showAddToCart={false}
      />
    );
  } else {
    preferredProductsBlock = (
      <PreferredProductsSection
        productFamilyName={product.preferredProductsFamilyName}
        preferredProducts={preferredProducts}
      />
    );
  }

  preferredProductsBlock = (
    <>
      {preferredProductsBlock}
      {preferredProducts.state !== "loading" && relatedProductsLoading && (
        <div className="scha__related-products">
          <CircleSpinner />
        </div>
      )}
    </>
  );

  const finalProps = {
    ...fullProps,
    postSummarySlot,
    productDescriptor,
    preferredProductsBlock: userIsLoggedIn() && !userIsRegistered() ? preferredProductsBlock : null,
  };

  const page = isDesktop ? <DesktopProductDetailsPage {...finalProps} /> : <MobileProductDetailsPage {...finalProps} />;

  return (
    <FormProvider {...formProps}>
      <PackageRestrictionContext value={restrictedCode}>{page}</PackageRestrictionContext>
      {(!userIsLoggedIn() || userIsRegistered()) && preferredProductsBlock}
      {relatedProducts && (
        <RelatedProductsBlock relatedProducts={relatedProducts} mainProductDesignation={product.designation} />
      )}
      <RecentlyViewedProducts variant={"pdp"} />
    </FormProvider>
  );
};

export const ProductDetailsPage = (props: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const product = props.product;
  const productCode = product.code;
  const [loaded, setLoaded] = useState(false);

  useOnMount(() => {
    dispatch(loadPDPData(product));

    trackPdpView(product, last(product.productAllCategories) ?? "");

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("redirect") !== "true") {
      if (urlParams.get("isPartialMatch") === "true") {
        dispatch(addMessage("info", t("page.pdp.toast.partialMatch")));
      }
      if (urlParams.get("isNewerVersion") === "true") {
        dispatch(addMessage("info", t("page.pdp.toast.newerVersion")));
      }
    }
    setLoaded(true);
  });

  const preferredProducts = useFetch(fetchPreferredProducts, productCode, {
    mode: "throttle",
    time: 1000,
    onLoad: (data) => {
      dispatch(addBaseProductDataAction(data?.sales ?? []));
    },
  });
  const { relatedProducts, state } = useRelatedProducts(productCode);
  const relatedProductsLoading = state === "loading";

  if (!loaded) {
    return null;
  }

  const fullProps: InnerProductDetailsPageProps = {
    productCode,
    designation: props.product.designation,
    preferredProducts,
    relatedProducts,
    relatedProductsLoading,
    showFBT: props.showFBT,
  };

  return <InnerProductDetailsPage {...fullProps} />;
};

interface ProductDetailsPageData {
  // ProductDataDTO as string
  product: string;
  // Tech
  technicalInfo: string;
  // Toggle for frequently bought together product
  showFBT: boolean;
  displayMMDAssistant: boolean;
}

export function createProductDetailsPage(data: ProductDetailsPageData) {
  const dto = JSON.parse(data.product) as PdpPageProductDataDto;
  const techInfo = JSON.parse(data.technicalInfo) as TechnicalInfoDataDTO;

  const product = transformPdpProduct(dto, techInfo);

  return (
    <PurchasePlaceProvider value={"PDP"}>
      <DisplayMMDAssistantProvider value={data.displayMMDAssistant}>
        <VariantContextProvider variantGroupProductList={product.variantGroupProductList}>
          <ProductDetailsPage product={product} showFBT={data.showFBT} />
        </VariantContextProvider>
      </DisplayMMDAssistantProvider>
    </PurchasePlaceProvider>
  );
}
